import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { adminEndpoints } from "@/lib/adminApi";
import { AdminPageHeader, AdminCard, EmptyState } from "@/components/admin/AdminUI";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminLeadsPage() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let cancelled = false;
        adminEndpoints
            .leads()
            .then((d) => {
                if (!cancelled) setLeads(d);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.detail || "Failed to load leads");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        if (!query.trim()) return leads;
        const q = query.trim().toLowerCase();
        return leads.filter(
            (l) =>
                (l.name || "").toLowerCase().includes(q) ||
                (l.email || "").toLowerCase().includes(q),
        );
    }, [leads, query]);

    return (
        <div data-testid="admin-leads-page">
            <AdminPageHeader
                eyebrow="Sales pipeline"
                title="Leads"
                description="Quote requests submitted via the marketing site contact form."
                testId="admin-leads-header"
                actions={
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-offwhite/50" />
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search name or email…"
                            className="w-72 border border-white/10 bg-white/[0.03] py-2 pl-10 pr-3 font-mono text-xs text-brand-offwhite placeholder:text-brand-offwhite/40 focus:border-brand-gold focus:outline-none"
                            data-testid="admin-leads-search"
                        />
                    </div>
                }
            />

            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}

            {loading ? (
                <AdminCard>Loading…</AdminCard>
            ) : filtered.length === 0 ? (
                <EmptyState
                    title={query ? "No matches" : "No leads yet"}
                    hint={
                        query
                            ? "Adjust the search term or clear it to see all leads."
                            : "Prospects who submit the contact form will show up here."
                    }
                    testId="admin-leads-empty"
                />
            ) : (
                <div
                    className="overflow-x-auto border border-white/10 bg-white/[0.02]"
                    data-testid="admin-leads-table-wrap"
                >
                    <table className="min-w-full text-left text-sm" data-testid="admin-leads-table">
                        <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Phone</th>
                                <th className="px-4 py-3">Service</th>
                                <th className="px-4 py-3">Budget</th>
                                <th className="px-4 py-3">Received</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((lead) => (
                                <tr
                                    key={lead.id}
                                    onClick={() => setSelected(lead)}
                                    className="cursor-pointer border-b border-white/5 last:border-0 hover:bg-white/[0.04]"
                                    data-testid={`admin-lead-row-${lead.id}`}
                                >
                                    <td className="px-4 py-3 font-body text-brand-offwhite">{lead.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/80">{lead.email}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/80">{lead.phone}</td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                        {lead.service_of_interest}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-gold">
                                        {lead.budget_range}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                        {formatDate(lead.created_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setSelected(null)}
                    data-testid="admin-lead-drawer-backdrop"
                >
                    <div
                        className="relative max-h-[80vh] w-full max-w-2xl overflow-y-auto border border-white/10 bg-brand-charcoal p-8"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="admin-lead-drawer"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                            Lead detail
                        </p>
                        <h3 className="mt-2 font-display text-3xl uppercase tracking-wide">
                            {selected.name}
                        </h3>
                        <dl className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                    Email
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-brand-offwhite">
                                    <a
                                        href={`mailto:${selected.email}`}
                                        className="text-brand-gold hover:underline"
                                    >
                                        {selected.email}
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                    Phone
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-brand-offwhite">
                                    {selected.phone}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                    Service
                                </dt>
                                <dd className="mt-1 font-body text-sm text-brand-offwhite">
                                    {selected.service_of_interest}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                    Budget
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-brand-gold">
                                    {selected.budget_range}
                                </dd>
                            </div>
                        </dl>
                        <div className="mt-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                Project details
                            </p>
                            <p className="mt-2 whitespace-pre-wrap border-l-2 border-brand-gold bg-white/[0.02] p-4 font-body text-sm leading-relaxed text-brand-offwhite/90">
                                {selected.project_details}
                            </p>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setSelected(null)}
                                className="border border-white/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                                data-testid="admin-lead-drawer-close"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
